import { useCallback, useMemo } from 'react'

import { useLocalStorage } from '@buffer-mono/popcorn'

import { columnVisibilityPreference } from './columnVisibilityPreference'

export interface MetricColumn {
  type: string
  displayName: string
}

/**
 * Scope for persisted column preferences. Pass `'all-channels'` for the
 * aggregate all-channels Performance table, or a channel id string for a
 * single-channel table. The literal hint lets IDEs suggest `'all-channels'`
 * while still accepting any channel id.
 */
export type ColumnVisibilityScope = 'all-channels' | (string & {})

interface UseColumnVisibilityOptions {
  /**
   * Available metric columns from the current backend response. May contain
   * duplicates across rows — the hook dedupes by `type`, preserving first
   * occurrence.
   */
  availableMetrics: MetricColumn[]
  /** Scope for persisted preferences. */
  scopeId: ColumnVisibilityScope
  /** Default number of columns to show initially (default: 4) */
  defaultCount?: number
  /** Minimum number of columns that must remain visible (default: 3) */
  minCount?: number
}

interface UseColumnVisibilityResult {
  /** All unique metrics: visible (in user order) first, then hidden (in backend order) */
  availableMetrics: MetricColumn[]
  /** Currently visible metrics, in user order */
  visibleMetrics: MetricColumn[]
  /** Set of currently selected metric types */
  selectedMetrics: ReadonlySet<string>
  /** Toggle a metric column on/off (respects minCount) */
  toggleMetric: (type: string) => void
  /**
   * Reorder metrics via drag-and-drop. Dragging never unchecks an item:
   * see `columnVisibilityPreference` for the full rule table.
   */
  reorderMetric: (activeType: string, overType: string) => void
  /** Whether the column selector should be shown (more metrics than defaultCount) */
  showColumnSelector: boolean
}

type StoredValue = string[] | null

export const COLUMN_VISIBILITY_STORAGE_KEY_PREFIX = 'insights-columns:v2'

/**
 * Returns the localStorage key used to persist preferences for a given scope.
 * Exposed so tests and future cleanup utilities can match the same keys the
 * hook writes to.
 */
export function getColumnVisibilityStorageKey(
  scopeId: ColumnVisibilityScope,
): string {
  return scopeId === 'all-channels'
    ? `${COLUMN_VISIBILITY_STORAGE_KEY_PREFIX}:all-channels`
    : `${COLUMN_VISIBILITY_STORAGE_KEY_PREFIX}:channel:${scopeId}`
}

function isValidStored(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string')
}

/**
 * Manages column visibility and order for an Insights table, with per-scope
 * persistence via localStorage. Ordering + selection semantics live in the
 * pure `columnVisibilityPreference` helper — this hook is the React +
 * storage shell around it.
 */
export function useColumnVisibility({
  availableMetrics,
  scopeId,
  defaultCount = 4,
  minCount = 3,
}: UseColumnVisibilityOptions): UseColumnVisibilityResult {
  const backendMetrics = useMemo(() => {
    const seen = new Set<string>()
    const result: MetricColumn[] = []
    for (const metric of availableMetrics) {
      if (!seen.has(metric.type)) {
        seen.add(metric.type)
        result.push({ type: metric.type, displayName: metric.displayName })
      }
    }
    return result
  }, [availableMetrics])

  const backendTypes = useMemo(
    () => backendMetrics.map((m) => m.type),
    [backendMetrics],
  )

  const [storedRaw, setStored] = useLocalStorage<StoredValue>(
    getColumnVisibilityStorageKey(scopeId),
    null,
  )

  const stored = useMemo<string[] | null>(() => {
    if (storedRaw === null) return null
    if (isValidStored(storedRaw)) return storedRaw
    // eslint-disable-next-line no-console
    console.warn(
      `[useColumnVisibility] Ignoring invalid persisted value at "${getColumnVisibilityStorageKey(
        scopeId,
      )}"; falling back to defaults.`,
    )
    return null
  }, [storedRaw, scopeId])

  const preference = useMemo(
    () =>
      columnVisibilityPreference(stored, backendTypes, {
        defaultCount,
        minCount,
      }),
    [stored, backendTypes, defaultCount, minCount],
  )

  const visibleSet = useMemo(
    () => new Set(preference.visible),
    [preference.visible],
  )

  const availableOrdered = useMemo<MetricColumn[]>(() => {
    const byType = new Map(backendMetrics.map((m) => [m.type, m]))
    const hidden = backendTypes.filter((t) => !visibleSet.has(t))
    return [...preference.visible, ...hidden]
      .map((t) => byType.get(t))
      .filter((m): m is MetricColumn => m !== undefined)
  }, [preference.visible, backendTypes, backendMetrics, visibleSet])

  const visibleMetrics = useMemo<MetricColumn[]>(() => {
    const byType = new Map(backendMetrics.map((m) => [m.type, m]))
    return preference.visible
      .map((t) => byType.get(t))
      .filter((m): m is MetricColumn => m !== undefined)
  }, [preference.visible, backendMetrics])

  const toggleMetric = useCallback(
    (type: string): void => {
      const next = preference.toggle(type)
      if (next !== null) setStored(next)
    },
    [preference, setStored],
  )

  const reorderMetric = useCallback(
    (activeType: string, overType: string): void => {
      const next = preference.reorder(activeType, overType)
      if (next !== null) setStored(next)
    },
    [preference, setStored],
  )

  return {
    availableMetrics: availableOrdered,
    visibleMetrics,
    selectedMetrics: visibleSet,
    toggleMetric,
    reorderMetric,
    showColumnSelector: backendMetrics.length > defaultCount,
  }
}
